/**
 * useTaskCompletion - WebSocket-first task waiting with HTTP polling fallback.
 *
 * Provides a function to wait for a Celery task to complete via WebSocket.
 * Falls back to HTTP polling when WebSocket is not connected.
 *
 * This replaces taskAPI.pollForCompletion() in components that have
 * access to the WebSocket context.
 */

import { useCallback, useRef, useEffect } from 'react';
import { useTaskEvents, useWebSocketConnection } from '../context/WebSocketContext';
import { taskAPI } from '../services/api';
import { TaskStatus } from '../types';
import { taskFailureOutcome, unwrapTaskOutcome, type TaskCompletionResult } from './taskOutcome';

interface TaskCompletionOptions {
  /** Called with progress updates (from WebSocket or HTTP polling) */
  onProgress?: (status: { progress: number; message?: string }) => void;
  /** Max inactivity window in ms before giving up (default: 300000 = 5 min) */
  timeoutMs?: number;
  /** HTTP polling interval in ms when in fallback mode (default: 2000) */
  pollIntervalMs?: number;
}

const WS_DEBUG = process.env.EXPO_PUBLIC_DEBUG_WS === 'true';
const logDebug = (...args: any[]) => {
  if (WS_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};

/**
 * Hook that provides a `waitForTask` function.
 *
 * When WebSocket is connected, it subscribes to the task via WebSocket
 * and resolves the promise when `task_complete` or `task_error` arrives.
 *
 * When WebSocket is disconnected/fallback, it falls back to HTTP polling.
 */
export function useTaskCompletion() {
  const { isConnected, useFallback } = useWebSocketConnection();
  const { subscribe, unsubscribe, getTaskProgress } = useTaskEvents();

  const isConnectedRef = useRef(isConnected);
  const useFallbackRef = useRef(useFallback);
  useEffect(() => {
    isConnectedRef.current = isConnected;
  }, [isConnected]);
  useEffect(() => {
    useFallbackRef.current = useFallback;
  }, [useFallback]);

  // Track active waiters so cleanup works correctly
  const activeWaitersRef = useRef<Map<string, () => void>>(new Map());

  const waitForTask = useCallback(
    (taskId: string, options?: TaskCompletionOptions): Promise<TaskCompletionResult> => {
      const {
        onProgress,
        timeoutMs = 300_000,
        pollIntervalMs = 2000,
      } = options || {};

      // If WebSocket is not connected or in fallback mode, use HTTP polling
      if (!isConnected || useFallback) {
        logDebug('[TaskWait] Using polling (ws unavailable)', taskId, { isConnected, useFallback });
        return waitViaPolling(taskId, { onProgress, timeoutMs, pollIntervalMs });
      }

      // WebSocket-based waiting
      return new Promise<TaskCompletionResult>((resolve, reject) => {
        let resolved = false;
        let checkInterval: ReturnType<typeof setInterval> | null = null;
        let statusCheckInFlight = false;
        let lastProgressRef: unknown = null;
        let lastProgressAt = Date.now();
        let lastProgressSignature: string | null = null;
        let lastHttpStatusSignature: string | null = null;
        let lastStatusCheckAt = 0;
        let inactivityTimeoutAt = Date.now() + timeoutMs;
        const safetyPollMs = Math.max(5000, pollIntervalMs * 2);

        const cleanup = () => {
          if (checkInterval) {
            clearInterval(checkInterval);
            checkInterval = null;
          }
          unsubscribe(taskId);
          activeWaitersRef.current.delete(taskId);
        };

        const bumpInactivityWindow = () => {
          inactivityTimeoutAt = Date.now() + timeoutMs;
        };

        const resolveOnce = (result: TaskCompletionResult) => {
          if (resolved) return;
          resolved = true;
          cleanup();
          logDebug('[TaskWait] Resolved', taskId, result.status);
          resolve(result);
        };

        const rejectOnce = (err: Error) => {
          if (resolved) return;
          resolved = true;
          cleanup();
          logDebug('[TaskWait] Rejected', taskId, err.message);
          reject(err);
        };

        const switchToPolling = () => {
          if (resolved) return;
          cleanup();
          logDebug('[TaskWait] Switching to polling', taskId, { timeoutMs });
          waitViaPolling(taskId, {
            onProgress,
            timeoutMs,
            pollIntervalMs,
          })
            .then((result) => resolveOnce(result))
            .catch((err) => rejectOnce(err instanceof Error ? err : new Error(String(err))));
        };

        // Store cleanup for external cancellation
        activeWaitersRef.current.set(taskId, cleanup);

        // Subscribe to task via WebSocket
        logDebug('[TaskWait] Subscribing to task', taskId);
        subscribe(taskId);

        // Poll the WebSocket context state (not HTTP!) to detect completion
        // The WebSocket context updates taskProgressMap when messages arrive
        checkInterval = setInterval(() => {
          if (resolved) return;

          if (!isConnectedRef.current || useFallbackRef.current) {
            switchToPolling();
            return;
          }

          const progress = getTaskProgress(taskId);
          const now = Date.now();
          if (progress) {
            const isPlaceholderProgress =
              !progress.isComplete &&
              !progress.error &&
              (progress.progress || 0) === 0 &&
              !progress.stage &&
              !progress.message;

            const signature = [
              progress.stage || '',
              progress.progress || 0,
              progress.message || '',
              progress.isComplete ? '1' : '0',
              progress.error || '',
            ].join('|');
            const isNewProgressEvent = progress !== lastProgressRef;

            if (!isPlaceholderProgress && isNewProgressEvent) {
              lastProgressRef = progress;
              lastProgressAt = now;
              bumpInactivityWindow();
            }

            if (!isPlaceholderProgress && signature !== lastProgressSignature) {
              lastProgressSignature = signature;
              // Report progress only when payload changes to avoid redundant UI updates.
              if (onProgress) {
                onProgress({
                  progress: progress.progress,
                  message: progress.message,
                });
              }
            }

            // Check for completion
            if (progress.isComplete) {
              if (progress.error) {
                resolveOnce(taskFailureOutcome(progress.error, progress.errorCode));
              } else {
                resolveOnce({
                  status: 'SUCCESS',
                  result: progress.result,
                });
              }
            }
          }
          
          // Safety net: if we haven't seen meaningful progress for a while, check status via HTTP.
          if (!statusCheckInFlight && now - lastProgressAt >= safetyPollMs && now - lastStatusCheckAt >= safetyPollMs) {
            statusCheckInFlight = true;
            logDebug('[TaskWait] Safety status check', taskId);
            taskAPI.getStatus(taskId)
              .then((status) => {
                const activityNow = Date.now();
                lastStatusCheckAt = activityNow;
                // Any successful status response means backend is alive; extend window.
                lastProgressAt = activityNow;
                bumpInactivityWindow();
                if (resolved) return;
                if (onProgress) {
                  onProgress({
                    progress: status.progress || 0,
                    message: status.message,
                  });
                }
                if (status.status === 'SUCCESS') {
                  logDebug('[TaskWait] Safety check success', taskId);
                  resolveOnce({ status: 'SUCCESS', result: status.result });
                } else if (status.status === 'FAILURE' || status.status === 'REVOKED') {
                  logDebug('[TaskWait] Safety check failure', taskId);
                  resolveOnce(taskFailureOutcome(status.error, status.error_code));
                } else {
                  const httpSignature = [
                    status.status,
                    status.progress ?? '',
                    status.message ?? '',
                  ].join('|');
                  lastHttpStatusSignature = httpSignature;

                  // Keep latest signature for inactivity diagnostics.
                }
              })
              .catch(() => {
                lastStatusCheckAt = Date.now();
              })
              .finally(() => {
                statusCheckInFlight = false;
              });
          }
          
          // Inactivity timeout: only fire when no meaningful progress was observed
          // for the configured timeout window.
          if (!statusCheckInFlight && now >= inactivityTimeoutAt && now - lastStatusCheckAt >= pollIntervalMs) {
            statusCheckInFlight = true;
            lastStatusCheckAt = now;
            logDebug('[TaskWait] Inactivity timeout reached', taskId);
            taskAPI
              .getStatus(taskId)
              .then((status) => {
                if (resolved) return;
                if (status.status === 'SUCCESS') {
                  resolveOnce({ status: 'SUCCESS', result: status.result });
                  return;
                }
                if (status.status === 'FAILURE' || status.status === 'REVOKED') {
                  resolveOnce(taskFailureOutcome(status.error, status.error_code));
                  return;
                }

                const httpSignature = [
                  status.status,
                  status.progress ?? '',
                  status.message ?? '',
                ].join('|');

                if (httpSignature !== lastHttpStatusSignature) {
                  // Backend still changing state - extend inactivity window.
                  lastHttpStatusSignature = httpSignature;
                  lastProgressAt = Date.now();
                  bumpInactivityWindow();
                  return;
                }
                rejectOnce(new Error('Task timed out waiting for progress updates'));
              })
              .catch(() => {
                rejectOnce(new Error('Task timed out while checking status'));
              })
              .finally(() => {
                statusCheckInFlight = false;
              });
          }
        }, 200); // Check context state every 200ms (no HTTP calls)
      });
    },
    [isConnected, useFallback, subscribe, unsubscribe, getTaskProgress]
  );

  /**
   * `waitForTask` for callers that want the task's result or an exception
   * (chat send / tool submit / audio flows, Library transcription). Replaces
   * the two screen-local copies of this logic (de-slop CHAT-18).
   * Default inactivity window: 15 minutes, as the local copies used.
   */
  const waitForTaskResult = useCallback(
    async <T = unknown>(
      taskId: string,
      options?: TaskCompletionOptions,
    ): Promise<T | null> => {
      const outcome = await waitForTask(taskId, { timeoutMs: 900_000, ...options });
      return unwrapTaskOutcome<T>(outcome);
    },
    [waitForTask]
  );

  return { waitForTask, waitForTaskResult, isWebSocketConnected: isConnected && !useFallback };
}

/**
 * HTTP polling fallback - same behavior as the old taskAPI.pollForCompletion()
 * but wrapped in our interface.
 */
async function waitViaPolling(
  taskId: string,
  options: {
    onProgress?: (status: { progress: number; message?: string }) => void;
    timeoutMs: number;
    pollIntervalMs: number;
  }
): Promise<TaskCompletionResult> {
  const { onProgress, timeoutMs, pollIntervalMs } = options;
  let lastActivityAt = Date.now();

  while (true) {
    try {
      const status: TaskStatus = await taskAPI.getStatus(taskId);
      lastActivityAt = Date.now();

      if (onProgress) {
        onProgress({
          progress: status.progress || 0,
          message: status.message,
        });
      }

      if (status.status === 'SUCCESS') {
        return { status: 'SUCCESS', result: status.result };
      }

      if (status.status === 'FAILURE' || status.status === 'REVOKED') {
        return taskFailureOutcome(status.error, status.error_code);
      }
    } catch {
      if (Date.now() - lastActivityAt > timeoutMs) {
        throw new Error('Task polling stalled - no status activity');
      }
    }

    await new Promise((r) => setTimeout(r, pollIntervalMs));
    if (Date.now() - lastActivityAt > timeoutMs) {
      throw new Error('Task polling stalled - no status activity');
    }
  }
}
